// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGridFrontSide.js" company="Izenda">
// Copyright (c) 2015 Izenda, Inc.  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and  International
// Copyright Laws. Unauthorized reproduction,  reverse-engineering, and
// distribution of all or any portion of  the code contained in this file is
// strictly prohibited and may  result in severe civil and criminal penalties
// and will be  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES  ARE CONFIDENTIAL AND
// PROPRIETARY TRADE  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
// LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET  CONTROLS AS
// PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED  FILES OR ANY
// PORTION OF ITS CONTENTS SHALL AT NO TIME BE  COPIED, TRANSFERRED, SOLD,
// DISTRIBUTED, OR OTHERWISE MADE  AVAILABLE TO OTHER INDIVIDUALS WITHOUT
// EXPRESS WRITTEN CONSENT  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  ADDITIONAL
// RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartGridFrontSide component
 */

import React, { Component, PropTypes } from 'react';
import { observe } from 'mobx';
import { curry, getOr, isEmpty, omit } from 'lodash/fp';
import classnames from 'classnames';

import NoData from 'share/components/controls/Izenda.NoData';
import ReportPartGridVerticalHorizontal from './Izenda.ReportPartGridVerticalHorizontal';
import ReportPartGridSeparator from './Izenda.ReportPartGridSeparator';
import GrandTotalGrid from 'modules/ReportDesigner/Fields/ReportBody/ReportPartType/Izenda.GrandTotalGrid';
import constants, { DEFAULT_VALUE_PROPERTIES as defaultValue } from 'logic/data/Izenda.ReportPartGridConstants';
import ReportPartGridPivot from './Izenda.ReportPartGridPivot';
import { MESSAGES, SORT_ORDERS } from 'logic/data/Izenda.ReportPartFieldConstants';
import ReportPartTitle from '../FieldContainers/Izenda.ReportPartTitle';
import { REPORT_PART_MODE } from 'logic/data/Izenda.ReportBodyConstants';
import Paginator from 'share/components/controls/DataGrid/helpers/Izenda.Paginator';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import GridPaginatorAction from './Izenda.GridPaginatorAction';
import connect from 'share/enhancers/Izenda.Connect';
import action from './Izenda.ReportPartGridFrontSideAction';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';

const GridPaginator = GridPaginatorAction(Paginator);
const NUMBER_ONE = 1;
const NUMBER_TIMEOUT_FINISH_RENDER = 500;
@connect(action)
export default class ReportPartGridFrontSide extends Component {
  constructor(props: any) {
    super(props);

    const {
      reportPart: { reportPartContent }
    } = props;

    this.mainComponentKey = 'MainComponent';
    this.renderListeners = [];
    this.dispose = observe(reportPartContent, 'changeGridStyleStatus', newValue => {
      !newValue &&
        props.confirmMessage('info', constants.MESSAGES.M07).then(() => {
          reportPartContent.changeGridStyleStatus = true;
        });
    });
    this.disposeInconsitentFunction = observe(reportPartContent, 'inconsitentFunction', newValue => {
      newValue && props.confirmMessage('info', MESSAGES.M08);
    });

    this.buildChildPromises();
  }

  static propTypes: {
    reportPart: PropTypes.object
  };

  componentWillUnmount() {
    const { props, dispose, disposeInconsitentFunction } = this;
    dispose();
    disposeInconsitentFunction();
    props.refreshData && props.refreshData(false);
  }

  publishFinishedRender(): void {
    const { props, renderListeners } = this;

    if (props.printingMode) {
      PrintingUtils.publishFinishedRender(renderListeners, () => {
        props.finishedRender && props.fulfilledData && props.finishedRender();
      });
    } else {
      props.finishedRender && props.fulfilledData && props.finishedRender();
    }
  }

  buildChildPromises(): void {
    this.renderListeners.push(PrintingUtils.createPartRenderPromise(this.mainComponentKey));
  }

  componentDidUpdate() {
    // Set timeout for grid render
    setTimeout(() => {
      this.publishFinishedRender();
    }, NUMBER_TIMEOUT_FINISH_RENDER);
  }

  renderHeader = curry(
    (props: any, container: any, fieldElement: any, title: any, callBack: any): any => {
      const isBackSide = getOr(false, 'reportPart.isBackSide', props);
      const sortValue = props.resolveSortValue(container, fieldElement);
      const className = !isBackSide && getOr('', [sortValue, 'headerIcon'], SORT_ORDERS);
      const clazzName = classnames({ u_curPointer: !isBackSide });

      const defaultStyle = {
        fontFamily: defaultValue.HEADERS.FONT.FAMILY,
        fontSize: defaultValue.HEADERS.FONT.SIZE + 'px',
        fontWeight: defaultValue.HEADERS.FONT.BOLD ? 'bold' : 'normal',
        textAlign: defaultValue.HEADERS.ALIGNMENT || 'none',
        textDecoration: defaultValue.HEADERS.FONT.UNDERLINE ? 'underline' : 'none',
        fontStyle: defaultValue.HEADERS.FONT.ITALIC ? 'italic' : 'normal',
        color: defaultValue.HEADERS.FONT.COLOR,
        wordWrap: defaultValue.HEADERS.WORDWRAP ? 'break-word' : 'normal',
        whiteSpace: defaultValue.HEADERS.WORDWRAP ? 'normal' : 'nowrap',
        display: defaultValue.HEADERS.DISPLAY
      };
      if (fieldElement.hiddenSortFeature) {
        return title;
      }

      return (
        <span
          className={clazzName}
          onClick={props.onSortChange(container, fieldElement, sortValue, props.previewRecords)}
        >
          <a>{title}</a>
          <i className={className} style={defaultStyle} />
        </span>
      );
    }
  );

  renderPrintingMode = () => {
    const {
      props,
      props: {
        printingMode,
        exportFormatSetting,
        reportPart,
        reportPart: {
          reportPartContent: { usePageBreakAfterSeparator }
        }
      }
    } = this;

    return (
      <div style={PrintingUtils.getReportPartContainerStyle(props)}>
        <div className={PrintingUtils.getReportPartClassName(props)}>
          {!usePageBreakAfterSeparator && (
            <ReportPartTitle
              mode={REPORT_PART_MODE.PREVIEW}
              reportPart={reportPart}
              className='u_marginBottom izenda-reportPartGrid_title'
            />
          )}
          {this.renderMainComponent(true)}
          <GrandTotalGrid
            reportPart={reportPart}
            staticRender
            printingMode={printingMode}
            exportFormatSetting={exportFormatSetting}
          />
        </div>
      </div>
    );
  };

  renderNormalMode = () => {
    const {
      props: { viewMode, reportPart, printingMode },
      renderMainComponent,
      goToPage,
      onPageSizeChange
    } = this;

    return (
      <GridLayout className='izenda-GridFrontSide'>
        <ReportPartTitle mode={REPORT_PART_MODE.PREVIEW} reportPart={reportPart} />
        <GridLayout.Row>
          <GridLayout.Col cols='12'>{renderMainComponent()}</GridLayout.Col>
        </GridLayout.Row>
        <GridLayout.Row>
          <GrandTotalGrid reportPart={reportPart} />
        </GridLayout.Row>
        {!printingMode && (
          <GridLayout.Row>
            <GridLayout.Col cols='12'>
              <GridPaginator
                onChange={goToPage}
                onDropdownChange={onPageSizeChange}
                reportPart={reportPart}
                canChangePageSize={!viewMode}
              />
            </GridLayout.Col>
          </GridLayout.Row>
        )}
      </GridLayout>
    );
  };

  goToPage = (pageIndex: string): void => {
    const {
      props,
      props: { reportPart }
    } = this;
    props.needUpdatePreview(reportPart, null, pageIndex, null, null, this.props.previewRecords);
  };

  onPageSizeChange = (pageSize: string): void => {
    const {
      props,
      props: { reportPart }
    } = this;

    reportPart.reportPartContent.changePageSize(pageSize);
    props.needUpdatePreview(reportPart, null, NUMBER_ONE, null, null, this.props.previewRecords);
  };

  renderMainComponent = (staticRender: boolean) => {
    const {
      props,
      props: {
        reportPart,
        reportPart: { previewDataRecords }
      }
    } = this;

    if (!reportPart.isBeingBuild) {
      return null;
    }
    if (!isEmpty(previewDataRecords)) {
      const GridComponent = this.getGridByName();
      const restProps = omit(['finishedRender', 'onSortChange'], props);

      if (reportPart.reportPartContent.useSeparator) {
        return (
          <ReportPartGridSeparator
            records={previewDataRecords}
            mainComponent={GridComponent}
            {...restProps}
            renderHeader={this.renderHeader(props)}
            finishedRender={PrintingUtils.getFinishedRenderPromiseById(this.renderListeners, this.mainComponentKey)}
          />
        );
      }
      return (
        <GridComponent
          records={previewDataRecords}
          {...restProps}
          staticRender={staticRender}
          renderHeader={this.renderHeader(props)}
          finishedRender={PrintingUtils.getFinishedRenderPromiseById(this.renderListeners, this.mainComponentKey)}
        />
      );
    }

    return reportPart.isLoadingData ? null : <NoData />;
  };

  getGridByName = (): any => {
    const gridStyle = this.props.reportPart.reportPartContent.properties.generalInfo.gridStyle;

    switch (gridStyle) {
      case constants.GRID_STYLES.PIVOT.value: // Pivot
        return ReportPartGridPivot;
      case constants.GRID_STYLES.DRILL_DOWN.value: // Drilldown
        return ReportPartGridPivot;
      default:
        return ReportPartGridVerticalHorizontal;
    }
  };

  render() {
    const {
      props,
      props: { reportPart, printingMode },
      renderPrintingMode,
      renderNormalMode
    } = this;

    // refresh data intervals
    props.refreshData && props.refreshData(true);

    if (ReportPartUtils.needToRenderLazyLoadingBar(reportPart, printingMode)) {
      return ReportPartUtils.renderLazyLoadingBar();
    }

    return printingMode ? renderPrintingMode() : renderNormalMode();
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGrid/Izenda.ReportPartGridFrontSide.js